// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PageTrackingWrapper from './posthog/PageTrackingWrapper';
import { useIdleTracker } from './posthog/hooks/useIdleTracker';

// Core Components & Pages
import AuthPage from './components/Auth/AuthPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import DashboardOverview from './pages/DashboardOverview';
import ProtectedRoute from './components/Layout/ProtectedRoute';
import ClosedAccountGuard from './components/Layout/ClosedAccountGuard';
import ProfilePage from './pages/ProfilePage';
import HelpPage from './pages/HelpPage';
import SingleListingGeneratorPage from './pages/SingleListingGenerator';
import BulkListingGeneratorPage from './pages/BulkListingGenerator';
import ReferralPage from './pages/Referral';
import OnboardingFlow from './pages/OnboardingFlow';
import ManagePage from './pages/Manage';
import AmazonSubmissionsPage from './pages/MarketPlaces/AmazonSubmissionsPage';
import MarketPlacePage from './pages/MarketPlaces';
import CataloguePage from './pages/MarketPlaces/CataloguePage';
import CatalogueHealth from './pages/SellerIntelligence/CatalogueHealth/CatalogueHealth';
import FixesPage from './pages/SellerIntelligence/CatalogueHealth/components/FixesPage';
import SearchTerms from './pages/SellerIntelligence/SearchTerms/SearchTerms';
import Visibility from './pages/SellerIntelligence/Visibility/Visibility';
import TermPerformance from './pages/SellerIntelligence/TermPerformance/TermPerformance';
import CTROptimizer from './pages/SellerIntelligence/CTROptimizer/CTROptimizer';
import KeywordPerformance from './pages/SellerIntelligence/KeywordPerformance/KeywordPerformance';
import SearchAttribution from './pages/SellerIntelligence/SearchAttribution/SearchAttribution';
import MetricsPlayground from './pages/SellerIntelligence/MetricsPlayground/MetricsPlayground';
import ProfitDashboard from './pages/SellerIntelligence/ProfitMargin/ProfitDashboard';
import CostSetup from './pages/SellerIntelligence/ProfitMargin/CostSetup';
import SellerIntelligence from './pages/SellerIntelligence';
import SalesHub from './pages/SellerIntelligence/Sales/SalesHub';
import SalesPerformance from './pages/SellerIntelligence/Sales/SalesPerformance';
import ProductPerformance from './pages/SellerIntelligence/ProductPerformance/ProductPerformance';
import GeographicSales from './pages/SellerIntelligence/GeographicSales/GeographicSales';
import GeographicOrders from './pages/SellerIntelligence/GeographicOrders/GeographicOrders';
import Seasonal from './pages/SellerIntelligence/Seasonal/Seasonal';
import PricingHub from './pages/SellerIntelligence/Pricing/PricingHub';
import Pricing from './pages/SellerIntelligence/Pricing/Pricing';
import Promotions from './pages/SellerIntelligence/Promotions/Promotions';
import PromoCalendar from './pages/SellerIntelligence/PromoCalendar/PromoCalendar';
import OrdersHub from './pages/SellerIntelligence/Orders/OrdersHub';
import OrderAnalytics from './pages/SellerIntelligence/Orders/OrderAnalytics';
import OrderFulfillment from './pages/SellerIntelligence/Orders/OrderFulfillment';
import AdsAnalytics from './pages/SellerIntelligence/AdsAnalytics/AdsAnalytics';
import AdsHealth from './pages/SellerIntelligence/AdsHealth/AdsHealth';
import KeywordOptimiser from './pages/SellerIntelligence/KeywordOptimiser/KeywordOptimiser';
import BudgetOptimiser from './pages/SellerIntelligence/BudgetOptimiser/BudgetOptimiser';
import AdsSetup from './pages/SellerIntelligence/AdsSetup/AdsSetup';
import InventoryHealth from './pages/SellerIntelligence/InventoryHealth/InventoryHealth';
import InventoryPerformance from './pages/SellerIntelligence/InventoryPerformance/InventoryPerformance';
import RestockRecommendations from './pages/SellerIntelligence/RestockRecommendations/RestockRecommendations';
import RestockAlerts from './pages/SellerIntelligence/RestockAlerts/RestockAlerts';
import SIHub from './pages/SellerIntelligence/SIHub/SIHub';
// --- Import FeedbackPage ---
import FeedbackPage from './pages/FeedbackPage';
// --- Import SupportTicketsPage ---
import SupportTicketsPage from './pages/SupportTicketsPage';
// --- Import ListingScore Component ---
import ListingScore from './components/ListingScore/ListingScore';
// --- Import BulkListingScore Component ---
import BulkListingScore from './components/ListingScore/bulk_listing_score/BulkListingScore';
// --- Import ListingScoreResultHistory Component ---
import ListingScoreResultHistory from './components/ListingScore/bulk_listing_score/ListingScoreResultHistory';
import ListingScoreDetailPage from './pages/ListingScoreDetail/ListingScoreDetailPage';
import UpdateTitlePage from './pages/UpdateTitle';
import UpdateTitleResultsPage from './pages/UpdateTitle/UpdateTitleResultsPage';
// --- Import Tools Components ---
import CheckDealTags from './pages/Tools/CheckDealTags';
import ImageZipUploader from './pages/Tools/ImageZipUploader';
// --- Import Legal Pages ---
import TermsOfUsePage from './pages/Legal/TermsOfUsePage';
import PrivacyPolicyPage from './pages/Legal/PrivacyPolicyPage';
import { TERMS_OF_USE_PATH, PRIVACY_POLICY_PATH } from './data/legal';
import AmazonReports from './pages/Tools/AmazonReports';
// Import SessionExpiredModal
import SessionExpiredModal from './components/Auth/SessionExpiredModal';
// Import Notification System
import { NotificationProvider } from './context/NotificationContext';
import GlobalNotification from './components/UI/GlobalNotification';
// Import ReferralHandler
import ReferralHandler from './components/ReferralHandler';
// Import FormStepProvider
import { FormStepProvider } from './context/FormStepContext';
import { AmazonSyncProvider } from './context/AmazonSyncContext';
import { TutorialProvider } from './context/TutorialContext';

// Placeholder imports for other pages/components (uncomment and use actual components when ready)
// import SettingsPage from './pages/SettingsPage';
import MySubscription from './pages/Plans/MySubscription';
import UsageHistoryPage from './pages/Plans/UsageHistoryPage';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css'; // Global styles

const queryClient = new QueryClient();

// Landing site lives on a separate domain — anyone landing on legacy
// in-app routes (/home, /about, etc.) should be sent there instead.
const LANDING_HOST = process.env.REACT_APP_LANDING_URL || '';
const ExternalRedirect = () => {
  const location = useLocation();
  useEffect(() => {
    if (!LANDING_HOST) return;
    window.location.replace(`${LANDING_HOST}${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
  return null;
};

function App() {
  useIdleTracker();
  return (
    <QueryClientProvider client={queryClient}>
    <NotificationProvider>
      {/* Session Expired Modal - shown conditionally based on AuthContext state */}
      <SessionExpiredModal />

      {/* Global Notification System */}
      <GlobalNotification />

      <Router>
        <PageTrackingWrapper />
        <Routes>
          {/* --- Auth Page Route --- */}
          <Route path="/auth" element={<AuthPage />} />
          {/* --- Root --- */}
          <Route path="/" element={<Navigate replace to="/auth" />} />
          {/* --- Legacy landing routes — redirect to marketing site --- */}
          <Route path="/home" element={<ExternalRedirect />} />
          <Route path="/about" element={<ExternalRedirect />} />
          <Route path="/contact" element={<ExternalRedirect />} />
          <Route path="/pricing" element={<ExternalRedirect />} />
          <Route path="/solutions" element={<ExternalRedirect />} />
          <Route path="/blogs" element={<ExternalRedirect />} />
          <Route path="/blogs/:slug" element={<ExternalRedirect />} />
          <Route path="/thank-you" element={<ExternalRedirect />} />
          {/* --- Legal Routes (public — linked from signup consent) --- */}
          <Route path={TERMS_OF_USE_PATH} element={<TermsOfUsePage />} />
          <Route path={PRIVACY_POLICY_PATH} element={<PrivacyPolicyPage />} />
          {/* --- Protected Dashboard Routes --- */}
          <Route element={<ProtectedRoute />}>
            <Route path="/onboarding" element={<OnboardingFlow />} />
          </Route>
          <Route element={<ProtectedRoute requireQuestionnaire />}> {/* This wrapper protects all nested routes */}
            <Route path="/dashboard" element={
              <FormStepProvider>
                <AmazonSyncProvider>
                  <TutorialProvider>
                    <DashboardLayout />
                  </TutorialProvider>
                </AmazonSyncProvider>
              </FormStepProvider>
            }>
              {/* Index route for the dashboard */}
              <Route index element={<DashboardOverview />} />

              {/* --- Generator Routes --- */}
              <Route path="generate/single" element={<SingleListingGeneratorPage />} />
              <Route path="generate/bulk" element={<BulkListingGeneratorPage />} />

              {/* --- Listing Score Routes --- */}
              <Route path="listing-score" element={<ListingScore />} />
              <Route path="bulk-listing-score" element={<BulkListingScore />} />
              <Route path="listing-score-results" element={<ListingScoreResultHistory />} />
              <Route path="listing-score-detail" element={<ListingScoreDetailPage />} />
              <Route path="market-places" element={<MarketPlacePage />} />
              <Route path="catalogue" element={<CataloguePage />} />
              {/* Needs a catalogue: close accounts must connect Amazon first,
                  open accounts work off external ASINs and pass through. */}
              <Route path="update-title" element={<ClosedAccountGuard><UpdateTitlePage /></ClosedAccountGuard>} />
              <Route path="update-title-results" element={<UpdateTitleResultsPage />} />
              {/* Section index. Every Seller Intelligence page links back here,
                  and domains that will hold several dashboards open their own
                  index rather than crowding this one. */}
              <Route path="seller-intelligence" element={<SellerIntelligence />} />
              <Route path="seller-intelligence/sales" element={<SalesHub />} />
              {/* Lists the dashboard's components. None are built yet. */}
              <Route path="seller-intelligence/sales/sales-performance" element={<SalesPerformance />} />
              {/* Explains the behaviour underneath the revenue numbers. */}
              <Route path="seller-intelligence/sales/product-performance" element={<ProductPerformance />} />
              <Route path="seller-intelligence/sales/geographic" element={<GeographicSales />} />
              {/* Units, not revenue: a price change must not read as demand. */}
              <Route path="seller-intelligence/sales/seasonal" element={<Seasonal />} />
              <Route path="seller-intelligence/pricing" element={<PricingHub />} />
              {/* Margin is the headline here, units are context. */}
              <Route path="seller-intelligence/pricing/pricing-strategy" element={<Pricing />} />
              {/* Redemptions are not return: the net decides. */}
              <Route path="seller-intelligence/pricing/promotions" element={<Promotions />} />
              {/* Nothing here submits or edits anything on Amazon. */}
              <Route path="seller-intelligence/pricing/promo-calendar" element={<PromoCalendar />} />
              <Route path="seller-intelligence/orders" element={<OrdersHub />} />
              <Route path="seller-intelligence/orders/order-analytics" element={<OrderAnalytics />} />
              {/* An operations board, so this page carries no window control. */}
              <Route path="seller-intelligence/orders/order-fulfillment" element={<OrderFulfillment />} />
              {/* Postal geography, not the state geography the sales pages use:
                  delivery follows courier routing, not administrative lines. */}
              <Route path="seller-intelligence/orders/geographic" element={<GeographicOrders />} />
              <Route path="seller-intelligence/catalogue-health" element={<CatalogueHealth />} />
              <Route path="seller-intelligence/inventory-health" element={<InventoryHealth />} />
              <Route path="seller-intelligence/inventory-performance" element={<InventoryPerformance />} />
              <Route path="seller-intelligence/restock" element={<RestockRecommendations />} />
              <Route path="seller-intelligence/restock-alerts" element={<RestockAlerts />} />
              <Route path="si" element={<SIHub />} />
              <Route path="seller-intelligence/fixes" element={<FixesPage />} />
              <Route path="seller-intelligence/search-terms" element={<SearchTerms />} />
              <Route path="seller-intelligence/visibility" element={<Visibility />} />
              <Route path="seller-intelligence/term-performance" element={<TermPerformance />} />
              <Route path="seller-intelligence/ctr" element={<CTROptimizer />} />
              <Route path="seller-intelligence/keyword-performance" element={<KeywordPerformance />} />
              <Route path="seller-intelligence/search-attribution" element={<SearchAttribution />} />
              {/* Dev harness for the metric library. The backend routes it calls
                  only register when FLASK_DEBUG or ENABLE_METRICS_PLAYGROUND is set,
                  so the page explains itself rather than erroring in production. */}
              <Route path="seller-intelligence/metrics-playground" element={<MetricsPlayground />} />
              {/* Profit & Margin: a reading page and an entry page, kept apart
                  because editing costs inside a reading surface does neither well. */}
              <Route path="seller-intelligence/profit" element={<ProfitDashboard />} />
              <Route path="seller-intelligence/cost-setup" element={<CostSetup />} />
              <Route path="seller-intelligence/ads-analytics" element={<AdsAnalytics />} />
              <Route path="seller-intelligence/ads-health" element={<AdsHealth />} />
              <Route path="seller-intelligence/keyword-optimiser" element={<KeywordOptimiser />} />
              <Route path="seller-intelligence/budget-optimiser" element={<BudgetOptimiser />} />
              <Route path="seller-intelligence/ads-setup" element={<AdsSetup />} />

              {/* --- Tools Routes (deal tag checker temporarily disabled) --- */}
              <Route path="tools/check-deal-tags" element={<CheckDealTags />} />
              <Route path="tools/image-zip-upload" element={<ImageZipUploader />} />
              <Route path="tools/amazon-reports" element={<AmazonReports />} />

              {/* Other Dashboard Sections */}
              <Route path="projects" element={<ManagePage />} />
              <Route path="projects/:project_id" element={<ManagePage />} />
              <Route path="amazon-submissions" element={<AmazonSubmissionsPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="referral" element={<ReferralPage />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="settings" element={<div><h1>Settings</h1>{/* <SettingsPage /> */}</div>} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="support-tickets" element={<SupportTicketsPage />} />
              <Route path="plans" element={<MySubscription />} />
              <Route path="usage-history" element={<UsageHistoryPage />} />

              {/* Catch-all for non-existent dashboard routes */}
              <Route path="*" element={<div><h2>404 - Dashboard Page Not Found</h2><p>Oops! The page you're looking for inside the dashboard doesn't exist.</p></div>} />
            </Route>
          </Route>

          {/* --- Global Catch-all --- */}
          {/* Check if it's a referral code, otherwise redirect to home */}
          <Route path="*" element={<ReferralHandler />} />
        </Routes>
      </Router>
    </NotificationProvider>
    </QueryClientProvider>
  );
}

export default App;